/**
 * Office.js helpers for reading the current PowerPoint document and its URL.
 */

/**
 * Reads the current presentation as a compressed (PPTX) Blob.
 */
export function getCurrentFileAsBlob(): Promise<Blob> {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 },
      async result => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message ?? 'Failed to get file'));
          return;
        }

        const file = result.value;
        const slices: ArrayBuffer[] = [];

        try {
          for (let i = 0; i < file.sliceCount; i++) {
            const slice = await new Promise<Office.Slice>((res, rej) => {
              file.getSliceAsync(i, r => {
                if (r.status === Office.AsyncResultStatus.Succeeded) {
                  res(r.value);
                } else {
                  rej(
                    new Error(r.error?.message ?? `Failed to get slice ${i}`)
                  );
                }
              });
            });
            slices.push(getArrayBufferFromSliceData(slice.data));
          }
        } finally {
          file.closeAsync(() => {});
        }

        const blob = new Blob(slices, {
          type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        });
        resolve(blob);
      }
    );
  });
}

/**
 * Returns the URL of the currently open document, or null if unavailable.
 * On SharePoint this will be the SharePoint file URL.
 */
export function getCurrentDocumentUrl(): string | null {
  try {
    return Office.context.document.url ?? null;
  } catch {
    return null;
  }
}

export function resolveCurrentDocumentUrl(): Promise<string | null> {
  const directUrl = getCurrentDocumentUrl();
  if (directUrl) {
    return Promise.resolve(directUrl);
  }

  return new Promise(resolve => {
    try {
      Office.context.document.getFilePropertiesAsync(result => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          resolve(null);
          return;
        }

        resolve(result.value.url ?? null);
      });
    } catch {
      resolve(null);
    }
  });
}

function getArrayBufferFromSliceData(
  data: ArrayBuffer | ArrayLike<number>
): ArrayBuffer {
  if (data instanceof ArrayBuffer) {
    return data;
  }

  return Uint8Array.from(data).buffer;
}
